import React, { FunctionComponent, ReactNode } from 'react'
import { Avatar, Button, Text, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './style.module.css'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import {
  convertConsumerDateOfBirth,
  useAppDispatch,
  useAppRouter,
  useConsumerContactAddress,
  useConsumerName,
  useConsumerSelector,
  useCountry,
  useExperiment,
} from '../../state'
import { Route } from '../../config/router'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { isCountryHardIdvSkipDocumentSelection } from '../../model'
import { InfoModal, InfoModalProps } from './infoModal'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import clsx from 'clsx'
import { Country } from '@afterpay/types'
import { setBiometricOptOut } from '../../state/identity/reducer'

export const VerifyStart: FunctionComponent = () => {
  const { t } = useTranslation()
  const { textSize, smallTextSize, sectionHeadingSize } = useCheckoutSizes()
  const { profile, email } = useConsumerSelector()
  const address = useConsumerContactAddress()
  const dateOfBirth = profile?.dateOfBirth
  const consumerName = useConsumerName()
  const router = useAppRouter()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const country = useCountry()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isConsumerAddressInQuebec = address?.countryCode === Country.CA && address?.state === 'QC'
  const caIdvQuebecExperiment = useExperiment('CAIdvQuebecEnabled')
  const caIdvQuebecFFEnabled = caIdvQuebecExperiment.active && !caIdvQuebecExperiment.control
  const isCaIdvQuebecEnabled = isConsumerAddressInQuebec && caIdvQuebecFFEnabled

  const dispatch = useAppDispatch()
  const reminderTranslation = t('verifyIdentity:start:reminder', { defaultValue: '' })
  const hasReminder = !isUndefinedOrEmpty(reminderTranslation) && reminderTranslation !== 'verifyIdentity:start:reminder'

  const handleStart = (biometricOptOut: boolean | null): void => {
    dispatch(setBiometricOptOut(biometricOptOut))
    logEvent(TrackingEvent.PRESSED_UPLOAD_MY_ID_BUTTON_ON_IDENTITY_START_PAGE)
    const skipDocumentSelection = isCountryHardIdvSkipDocumentSelection(country)
    if (skipDocumentSelection) {
      router.push(Route.VERIFY_BIOMETRIC_PERSONA)
    } else {
      router.push(Route.VERIFY_IDENTITY)
    }
  }

  const renderDocumentList = (): ReactNode => {
    const documentListOptions: Array<{ content: string; info?: InfoModalProps }> = t('verifyIdentity:start:documents', {
      returnObjects: true,
      defaultValue: [],
    })
    if (!Array.isArray(documentListOptions) || documentListOptions.length === 0) {
      return null
    }

    return (
      <ul className={styles.documentList}>
        {documentListOptions.map(
          (option, i) =>
            option !== null &&
            'content' in option && (
              <li key={`verify-start-document-${i}`} className={clsx(styles.documentItem, option.info != null && styles.documentItemWithInfo)}>
                <div className={styles.documentItemContent}>
                  <Text size={textSize}>{option.content}</Text>
                  {option.info != null && <InfoModal {...option.info} />}
                </div>
              </li>
            )
        )}
      </ul>
    )
  }

  return (
    <div className={clsx(styles.container, isBronteDesignLanguage && styles.withBorder)}>
      <MaskedElement actionName='Verify Info'>
        <div className={styles.profileCard}>
          <Avatar size='M' src='' alt={consumerName} name={consumerName} title={consumerName} />
          <div className='info'>
            <Text bold size={sectionHeadingSize}>
              {consumerName}
            </Text>
            <Text size={smallTextSize} color='Gray50'>
              {convertConsumerDateOfBirth({ dateOfBirth, locale: router.locale })}
            </Text>
            <Text size={smallTextSize} color='Gray50'>
              {email}
            </Text>
          </div>
        </div>
      </MaskedElement>
      <div className={styles.description}>
        <Text size={textSize}>{t('verifyIdentity:start:description')}</Text>
      </div>
      <Text size={textSize}>{t('verifyIdentity:start:detail')}</Text>
      {renderDocumentList()}
      {hasReminder && (
        <div className={styles.reminder}>
          <Text size={smallTextSize} color='Gray50'>
            {reminderTranslation}
          </Text>
        </div>
      )}
      {isCaIdvQuebecEnabled && (
        <div className={styles.quebec}>
          <Text size={smallTextSize} color='Gray50'>
            {t('verifyIdentity:start:quebecDescription')}
          </Text>
          <Button.Ghost testNameSpace='verify-verify-start-upload-quebec-id-only' padding='Fluid' onClick={() => handleStart(true)} size='Regular'>
            {t('verifyIdentity:start:ctaQuebecIdOnly')}
          </Button.Ghost>
        </div>
      )}
      <Button.Primary
        testNameSpace='verify-verify-start-upload'
        padding='Fluid'
        onClick={() => handleStart(caIdvQuebecFFEnabled ? false : null)}
        size='Regular'
      >
        {isCaIdvQuebecEnabled ? t('verifyIdentity:start:ctaQuebecWithSelfie') : t('verifyIdentity:start:cta')}
      </Button.Primary>
    </div>
  )
}
